import { DocumentTextIcon } from "@heroicons/react/24/outline"
import { Button } from "components/ui/button"
import { useEffect } from "react"
import { useUploadFiles } from "hooks/upload"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { useAddSettings, useSettingsData, useUpdateSettings } from "hooks/settings"
import { useNotificationBar } from "context/notificationContext"
import { usePopup } from "context/popupContext"
import { Download } from "lucide-react"

interface HTMLUploadForm {
    privacy_policy: File | string | null
    terms_and_conditions: File | string | null
    help_support: File | string | null
    about_us: File | string | null
}

const allowedFileNamesMap: Record<keyof HTMLUploadForm, string[]> = {
    privacy_policy: ['privacy_policy.html', 'privacy-policy.html'],
    terms_and_conditions: ['terms_and_conditions.html', 'terms-and-conditions.html'],
    help_support: ['help_support.html', 'help-support.html'],
    about_us: ['about.html']
}

const HTMLUploadSettings = () => {
    const notification = useNotificationBar()
    const popup = usePopup()

    const { mutate: addSettings, isLoading: addLoading } = useAddSettings()
    const { mutate: updateSettings, isLoading: updateLoading } = useUpdateSettings()
    const { data: settingsData, refetch } = useSettingsData()
    const uploadMutation = useUploadFiles()

    const htmlUploadSetting = settingsData?.data?.find(
        (item: any) => item.slug === 'html_upload_settings'
    )

    const methods = useForm<HTMLUploadForm>({
        mode: "onChange",
        defaultValues: {
            privacy_policy: null,
            terms_and_conditions: null,
            help_support: null,
            about_us: null
        }
    })

    const {
        handleSubmit,
        setValue,
        control,
        formState: { dirtyFields, errors },
        setError,
        clearErrors
    } = methods

    const watchedPrivacyPolicy = useWatch({ control, name: 'privacy_policy' })
    const watchedTermsAndConditions = useWatch({ control, name: 'terms_and_conditions' })
    const watchedHelpSupport = useWatch({ control, name: 'help_support' })
    const watchedAboutUs = useWatch({ control, name: 'about_us' })

    useEffect(() => {
        if (htmlUploadSetting?.settings) {
            setValue(
                'privacy_policy',
                htmlUploadSetting.settings.privacy_policy || null,
                { shouldDirty: false }
            )
            setValue(
                'terms_and_conditions',
                htmlUploadSetting.settings.terms_and_conditions || null,
                { shouldDirty: false }
            )
            setValue(
                'help_support',
                htmlUploadSetting.settings.help_support || null,
                { shouldDirty: false }
            )
            setValue(
                'about_us',
                htmlUploadSetting.settings.about_us || null,
                { shouldDirty: false }
            )
        }
    }, [htmlUploadSetting, setValue])

    const uploadHTML = async (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        // formData.append('customPath', 'settings/html')
        formData.append('customPath', 'static')

        const res = await uploadMutation.mutateAsync({
            data: formData,
        })

        return res?.data?.data?.key || ''
    }

    const handleDownload = async (fileKey: string, fileName: string) => {
        try {
            const baseUrl = process.env.REACT_APP_S3_BASE_URL || ''
            const fileUrl = `${baseUrl}/${fileKey}`

            const response = await fetch(fileUrl)
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            notification.push({
                title: 'Success',
                type: 'success',
                message: `${fileName} downloaded successfully`,
            })
        } catch (error) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: `Failed to download ${fileName}`,
            })
        }
    }

    const onSubmit = (data: HTMLUploadForm) => {
        if (Object.keys(errors).length > 0) {
            notification.push({
                title: 'Validation Error',
                type: 'error',
                message: 'Please fix validation errors before saving',
            })
            return
        }

        if (Object.keys(dirtyFields).length === 0) {
            notification.push({
                title: 'Info',
                type: 'info',
                message: 'No changes to save',
            })
            return
        }

        popup.open({
            title: 'Confirm Save',
            message: 'Save HTML Upload Settings?',
            type: 'confirm',
            confirmButtonText: 'Save',
            cancelButtonText: 'Cancel',
            onAction: async (confirmed: boolean) => {
                if (!confirmed) return

                let privacyPolicyKey = data.privacy_policy
                let termsAndConditionsKey = data.terms_and_conditions
                let helpSupportKey = data.help_support
                let aboutUsKey = data.about_us

                if (data.privacy_policy instanceof File) {
                    try {
                        privacyPolicyKey = await uploadHTML(data.privacy_policy)
                    } catch {
                        setError('privacy_policy', {
                            type: 'upload',
                            message: 'Failed to upload Privacy Policy'
                        })
                        return
                    }
                }

                if (data.terms_and_conditions instanceof File) {
                    try {
                        termsAndConditionsKey = await uploadHTML(data.terms_and_conditions)
                    } catch {
                        setError('terms_and_conditions', {
                            type: 'upload',
                            message: 'Failed to upload Terms and Conditions'
                        })
                        return
                    }
                }

                if (data.about_us instanceof File) {
                    try {
                        aboutUsKey = await uploadHTML(data.about_us)
                    } catch {
                        setError('about_us', {
                            type: 'upload',
                            message: 'Failed to upload About Us'
                        })
                        return
                    }
                }

                if (data.help_support instanceof File) {
                    try {
                        helpSupportKey = await uploadHTML(data.help_support)
                    } catch {
                        setError('help_support', {
                            type: 'upload',
                            message: 'Failed to upload Help & Support'
                        })
                        return
                    }
                }

                const payload = {
                    slug: "html_upload_settings",
                    settings: {
                        privacy_policy: privacyPolicyKey || htmlUploadSetting?.settings?.privacy_policy || '',
                        terms_and_conditions: termsAndConditionsKey || htmlUploadSetting?.settings?.terms_and_conditions || '',
                        help_support: helpSupportKey || htmlUploadSetting?.settings?.help_support || '',
                        about_us: aboutUsKey || htmlUploadSetting?.settings?.about_us || ''
                    },
                    status: 1,
                    is_delete: 0
                }

                if (htmlUploadSetting) {
                    updateSettings(
                        { data: payload, setting_id: htmlUploadSetting.id },
                        {
                            onSuccess: () => {
                                notification.push({
                                    title: 'Success',
                                    type: 'success',
                                    message: `HTML Upload settings saved successfully`,
                                })
                                refetch()
                            },
                            onError: () => {
                                notification.push({
                                    title: 'Error',
                                    type: 'error',
                                    message: `Something went wrong`,
                                })
                            }
                        }
                    )
                } else {
                    addSettings(
                        { data: payload },
                        {
                            onSuccess: () => {
                                notification.push({
                                    title: 'Success',
                                    type: 'success',
                                    message: `HTML Upload settings saved successfully`,
                                })
                                refetch()
                            },
                            onError: () => {
                                notification.push({
                                    title: 'Error',
                                    type: 'error',
                                    message: `Something went wrong`,
                                })
                            }
                        }
                    )
                }
            }
        })
    }

    const renderHTMLUploadSection = (
        name: keyof HTMLUploadForm,
        label: string,
        watchedValue: File | string | null,
        fixedFileName: string
    ) => {
        const fileName = watchedValue instanceof File ? watchedValue.name :
            (watchedValue ? watchedValue.split('/').pop() : null)

        return (
            <div className="p-4 border border-gray-200 dark:border-slate-700 rounded-lg">
                <h5 className="font-semibold mb-3 text-sm">{label}</h5>

                <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <label
                            htmlFor={name}
                            className="flex-1 flex justify-center items-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer hover:border-blue-400 transition text-sm"
                        >
                            <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-600 dark:text-gray-300">
                                {fileName || 'Browse HTML File'}
                            </span>
                        </label>

                        <input
                            id={name}
                            type="file"
                            className="hidden"
                            accept=".html,.htm"
                            // onChange={(e) => {
                            //     const file = e.target.files?.[0] ?? null

                            //     if (file && file.name !== fixedFileName) {
                            //         setError(name, {
                            //             type: 'validation',
                            //             message: `File name must be ${fixedFileName}`
                            //         })
                            //         e.target.value = ''
                            //         return
                            //     }

                            //     if (file) {
                            //         clearErrors(name)
                            //     }

                            //     setValue(name, file, { shouldDirty: true })
                            // }}
                            onChange={(e) => {
                                const file = e.target.files?.[0] ?? null

                                if (file) {
                                    const allowedNames = allowedFileNamesMap[name]

                                    if (!allowedNames.includes(file.name)) {
                                        setError(name, {
                                            type: 'validation',
                                            message: `Allowed file names: ${allowedNames.join(' OR ')}`
                                        })
                                        e.target.value = ''
                                        return
                                    }

                                    clearErrors(name)
                                }

                                setValue(name, file, { shouldDirty: true })
                            }}
                        />
                    </div>

                    {watchedValue && typeof watchedValue === 'string' && (
                        <Button
                            type="button"
                            color="basic"
                            onClick={() => handleDownload(watchedValue, fixedFileName)}
                            className="w-full"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                        </Button>
                    )}

                    {errors[name] && (
                        <p className="text-red-500 text-xs">
                            {errors[name]?.message as string}
                        </p>
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className='w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md'>
            <aside className="flex flex-auto items-center gap-2">
                <DocumentTextIcon className="h-6 w-6" />
                <h4 className="text-xl">HTML Upload Settings</h4>
            </aside>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="my-4">
                        <h4 className="font-semibold mb-4">Upload HTML Files</h4>
                        <hr className="my-2" />

                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                            {renderHTMLUploadSection('privacy_policy', 'Privacy Policy', watchedPrivacyPolicy, 'privacy_policy.html')}
                            {renderHTMLUploadSection('terms_and_conditions', 'Terms and Conditions', watchedTermsAndConditions, 'terms_and_conditions.html')}
                            {renderHTMLUploadSection('about_us', 'About Us', watchedAboutUs, 'about.html')}
                            {renderHTMLUploadSection('help_support', 'Help & Support', watchedHelpSupport, 'help_support.html')}
                        </div>
                    </div>

                    <div className="my-4">
                        <div className="flex justify-left">
                            <Button
                                color="blue"
                                type="submit"
                                disabled={Object.keys(dirtyFields).length === 0}
                                isLoading={updateLoading || addLoading}
                            >
                                Save HTML Upload Settings
                            </Button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default HTMLUploadSettings
